import { useQuery } from "@tanstack/react-query";
import { createContext, useContext } from "react";
import {
  getDriverInfo,
  getDriverLocations,
  getOrderById,
  getOrderLogs,
  getReceiverInfo,
  getSenderById,
  assignDriver as assignDriverService,
} from "services/order";
import { OrderTypeEnum } from "enums/shared";

const OrderContext = createContext();

export const OrderProvider = ({ orderId, children }) => {
  // fetch order details
  const {
    data: orderDetails,
    isLoading: orderLoading,
    isError: orderError,
  } = useQuery({
    queryKey: ["orderDetails", orderId],
    queryFn: () => getOrderById(orderId),
    enabled: !!orderId,
  });

  // fetch sender details
  const senderId = orderDetails?.senderId;
  const { data: senderDetails } = useQuery({
    queryKey: ["senderDetails", senderId],
    queryFn: () => getSenderById(senderId),
    enabled: !!senderId,
  });

  // fetch driver details
  const driverId = orderDetails?.driverId;
  const { data: driverDetails } = useQuery({
    queryKey: ["driverDetails", driverId],
    queryFn: () => getDriverInfo(driverId),
    enabled: !!driverId,
  });

  //fetch receiver
  const receiverId = orderDetails?.receiverId; // reciver receiverId

  const { data: receiverDetails } = useQuery({
    queryKey: ["receiverDetails", receiverId],
    queryFn: () => getReceiverInfo(receiverId),
    enabled: !!receiverId,
  });

  //fetch logs
  const {
    data: logsData,
    isLoading: logsLoading,
    isError: logsError,
  } = useQuery({
    queryKey: ["orderLogs", orderId],
    queryFn: () => getOrderLogs(orderId),
    enabled: !!orderId,
  });

  const orderLogs = logsData?.history || [];
  const orderIssues = logsData?.issues || [];

  // fetch drivers locations
  const { data: driverLocations } = useQuery({
    queryKey: ["driverLocations", orderDetails?.pickUpLocation?.coordinates, orderDetails?.type],
    queryFn: () => {
      // Map order type: 1 for Regular, 2 for Expedited
      const mappedOrderType = orderDetails?.type === OrderTypeEnum.expedited ? 2 : 1;
      return getDriverLocations(
        10, //here
        1,
        orderDetails.pickUpLocation.coordinates.latitude,
        orderDetails.pickUpLocation.coordinates.longitude,
        mappedOrderType
      );
    },
    enabled: !!orderDetails?.pickUpLocation?.coordinates,
  });

  const isLoading = orderLoading;
  const isError = orderError;

  return (
    <OrderContext.Provider
      value={{
        orderDetails,
        isLoading,
        isError,
        driverDetails,
        senderDetails,
        receiverDetails,
        orderLogs,
        orderIssues,
        logsLoading,
        logsError,
        orderId,
        driverLocations: driverLocations?.data?.page || [],
        assignDriver: assignDriverService,
      }}
    >
      {children}
    </OrderContext.Provider>
  );
};

export const useOrderContext = () => {
  return useContext(OrderContext);
};
